import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Play, Lock, Video, GraduationCap, Search, ShoppingCart, ArrowUp, Dumbbell, Apple, Pill, Sparkles, ChevronRight, FileText } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const Academia = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [contents, setContents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedLevel, setSelectedLevel] = useState('all');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [academiaConfig, setAcademiaConfig] = useState({ price: 19.99, enabled: true });
  const [processingPurchase, setProcessingPurchase] = useState(false);
  const [discountCode, setDiscountCode] = useState('');
  const [appliedDiscount, setAppliedDiscount] = useState(null);
  const [applyingDiscount, setApplyingDiscount] = useState(false);

  useEffect(() => {
    checkAuth();
    fetchContents();
    fetchAcademiaConfig();
  }, []);

  const fetchAcademiaConfig = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/site-config`);
      setAcademiaConfig({
        price: response.data.academia_price || 19.99,
        enabled: response.data.academia_enabled !== false
      });
    } catch (error) {
      console.error('Error fetching config:', error);
    }
  };

  const handleApplyDiscount = async () => {
    if (!discountCode.trim()) {
      toast.error('Ingresa un código de descuento');
      return;
    }
    
    setApplyingDiscount(true);
    try {
      const response = await axios.post(`${BACKEND_URL}/api/discounts/validate`, {
        code: discountCode.trim().toUpperCase(),
        amount: academiaConfig.price
      });
      
      if (response.data.valid) {
        setAppliedDiscount(response.data);
        toast.success(`¡Descuento aplicado! -$${response.data.discount_amount.toFixed(2)}`);
      } else {
        toast.error(response.data.message || 'Código inválido');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Código de descuento inválido');
    } finally {
      setApplyingDiscount(false);
    }
  };

  const handleBuyAcademiaAccess = async () => {
    setProcessingPurchase(true);
    try {
      const token = localStorage.getItem('session_token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      
      // Ir directo al checkout, sin pedir email
      // Después del pago exitoso, se pedirá crear cuenta si es necesario
      const response = await axios.post(`${BACKEND_URL}/api/payments/create-academia-checkout`, {
        origin_url: window.location.origin,
        discount_code: appliedDiscount ? discountCode.trim().toUpperCase() : ''
      }, { headers });

      if (response.data.url) {
        window.location.href = response.data.url;
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al procesar la compra');
    } finally {
      setProcessingPurchase(false);
    }
  };

  const checkAuth = async () => {
    const token = localStorage.getItem('session_token');
    if (token) {
      try {
        const response = await axios.get(`${BACKEND_URL}/api/auth/me`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setUser(response.data);
      } catch (error) {
        console.error('Auth error:', error);
      }
    }
  };

  const fetchContents = async () => {
    try {
      const token = localStorage.getItem('session_token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const response = await axios.get(`${BACKEND_URL}/api/content?category=academia`, { headers });
      setContents(response.data);
    } catch (error) {
      console.error('Error fetching contents:', error);
    } finally {
      setLoading(false);
    }
  };

  const canAccessContent = (content) => {
    const contentLevel = content.level;
    const contentLevels = content.levels || [contentLevel];
    
    if (!user) return contentLevels.includes('free') || contentLevel === 'free';
    // Material asignado a programas: el backend solo lo devuelve si la usuaria tiene alguno de esos programas
    if ((content.program_ids || []).length > 0) return true;
    
    // Si el usuario tiene acceso a Academia (compra individual), puede ver todo excepto premium exclusivo
    if (user.has_academia_access) {
      return true;
    }
    
    const accessLevels = {
      'free': ['free'],
      'principiante': ['free', 'nivel_1'],
      'intermedio': ['free', 'nivel_1', 'nivel_2'],
      'avanzado': ['free', 'nivel_1', 'nivel_2', 'nivel_3'],
      'premium': ['free', 'nivel_1', 'nivel_2', 'nivel_3', 'premium']
    };

    const userAllowedLevels = accessLevels[user.subscription_level] || ['free'];
    return contentLevels.some(lvl => userAllowedLevels.includes(lvl));
  };

  const levels = ['all', 'free', 'nivel_1', 'nivel_2', 'premium'];
  
  const academiaCategories = [
    { id: 'all', label: 'Todo', icon: GraduationCap, color: 'bg-zinc-600' },
    { id: 'entrenamiento', label: 'Entrenamiento', icon: Dumbbell, color: 'bg-orange-500' },
    { id: 'nutricion', label: 'Nutrición', icon: Apple, color: 'bg-green-500' },
    { id: 'suplementacion', label: 'Suplementación', icon: Pill, color: 'bg-purple-500' },
  ];

  const filteredContents = contents.filter(content => {
    const matchesSearch = content.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          content.description?.toLowerCase().includes(searchTerm.toLowerCase());
    // Premium muestra TODO el contenido
    const matchesLevel = selectedLevel === 'all' || 
                         selectedLevel === 'premium' || 
                         content.levels?.includes(selectedLevel) ||
                         content.level === selectedLevel;
    const matchesCategory = selectedCategory === 'all' || content.academia_category === selectedCategory;
    return matchesSearch && matchesLevel && matchesCategory;
  });

  // Ordenar: contenido accesible primero, bloqueado después
  const sortedContents = [...filteredContents].sort((a, b) => {
    const aAccessible = canAccessContent(a);
    const bAccessible = canAccessContent(b);
    
    // Accesible antes que bloqueado
    if (aAccessible && !bAccessible) return -1;
    if (!aAccessible && bAccessible) return 1;
    
    // Si ambos tienen el mismo acceso, ordenar por fecha (más reciente primero)
    return 0;
  });

  const levelNames = {
    'free': 'Gratis',
    'nivel_1': 'Nivel 1',
    'nivel_2': 'Nivel 2',
    'premium': 'Premium'
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#09090b] flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-pink-500"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#09090b] py-24">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 bg-pink-500/10 border border-pink-500/30 rounded-full px-4 py-2 mb-6">
            <Sparkles className="w-4 h-4 text-pink-400" />
            <span className="text-pink-400 text-sm font-medium uppercase tracking-wider">Biblioteca Educativa</span>
          </div>
          
          <h1 className="font-barlow text-4xl md:text-6xl font-black uppercase text-white mb-4" data-testid="academia-title">
            ACADEMIA
          </h1>
          <p className="text-lg text-zinc-400 max-w-2xl mx-auto">
            Biblioteca educativa con videos de entrenamiento, nutrición y suplementación
          </p>
        </div>

        {/* Banner de Compra de Academia - Solo mostrar si no tiene acceso completo */}
        {academiaConfig.enabled && (!user || (user.subscription_level === 'free' && !user.has_academia_access)) && (
          <div className="bg-gradient-to-r from-pink-600 to-purple-600 rounded-3xl p-8 mb-8 relative overflow-hidden">
            <div className="absolute inset-0 bg-black/20" />
            <div className="absolute top-0 right-0 w-64 h-64 bg-white/10 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2" />
            
            <div className="relative z-10 flex flex-col md:flex-row items-center justify-between gap-6">
              <div className="text-center md:text-left">
                <h3 className="text-2xl md:text-3xl font-bold text-white mb-2">
                  🎓 Acceso Completo a la Academia
                </h3>
                <p className="text-pink-100 text-lg mb-2">
                  Desbloquea todos los videos de entrenamiento, nutrición y suplementación
                </p>
                <div className="flex flex-wrap gap-3 justify-center md:justify-start">
                  <span className="bg-white/20 px-3 py-1 rounded-full text-sm text-white">✓ Todos los videos</span>
                  <span className="bg-white/20 px-3 py-1 rounded-full text-sm text-white">✓ Sin restricciones</span>
                  <span className="bg-white/20 px-3 py-1 rounded-full text-sm text-white">✓ Acceso permanente</span>
                </div>
              </div>
              
              <div className="text-center">
                <div className="text-4xl md:text-5xl font-black text-white mb-2">
                  {appliedDiscount ? (
                    <>
                      <span className="line-through text-2xl text-pink-200 mr-2">${academiaConfig.price}</span>
                      ${appliedDiscount.final_amount.toFixed(2)}
                    </>
                  ) : (
                    `$${academiaConfig.price}`
                  )}
                  <span className="text-lg font-normal text-pink-200"> USD</span>
                </div>
                <p className="text-pink-200 text-sm mb-3">Pago único</p>
                
                {/* Campo de código de descuento */}
                <div className="flex gap-2 mb-4 max-w-xs mx-auto">
                  <Input
                    type="text"
                    placeholder="Código de descuento"
                    value={discountCode}
                    onChange={(e) => setDiscountCode(e.target.value.toUpperCase())}
                    className="bg-white/20 border-white/30 text-white placeholder:text-pink-200 text-center"
                    disabled={!!appliedDiscount}
                    data-testid="academia-discount-input"
                  />
                  {!appliedDiscount ? (
                    <Button
                      onClick={handleApplyDiscount}
                      disabled={applyingDiscount || !discountCode.trim()}
                      className="bg-white/20 hover:bg-white/30 text-white border border-white/30 px-4"
                      data-testid="academia-apply-discount-btn"
                    >
                      {applyingDiscount ? '...' : 'Aplicar'}
                    </Button>
                  ) : (
                    <Button
                      onClick={() => {
                        setAppliedDiscount(null);
                        setDiscountCode('');
                      }}
                      className="bg-red-500/20 hover:bg-red-500/30 text-white border border-red-500/30 px-4"
                    >
                      ✕
                    </Button>
                  )}
                </div>
                {appliedDiscount && (
                  <p className="text-green-300 text-sm mb-3">
                    ✓ Descuento: -${appliedDiscount.discount_amount.toFixed(2)}
                  </p>
                )}
                
                <Button
                  onClick={handleBuyAcademiaAccess}
                  disabled={processingPurchase}
                  className="bg-white text-pink-600 hover:bg-pink-50 px-8 py-6 rounded-full font-bold text-lg shadow-xl hover:scale-105 transition-all"
                  data-testid="buy-academia-btn"
                >
                  {processingPurchase ? (
                    <span className="flex items-center gap-2">
                      <div className="w-5 h-5 border-2 border-pink-600 border-t-transparent rounded-full animate-spin" />
                      Procesando...
                    </span>
                  ) : (
                    <span className="flex items-center gap-2">
                      <ShoppingCart className="w-5 h-5" />
                      Comprar Acceso
                    </span>
                  )}
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Badge de acceso si el usuario tiene Academia */}
        {user?.has_academia_access && (
          <div className="bg-green-500/20 border border-green-500/30 rounded-2xl p-4 mb-8 flex items-center justify-center gap-3">
            <GraduationCap className="w-6 h-6 text-green-400" />
            <span className="text-green-400 font-medium">Tienes acceso completo a la Academia</span>
          </div>
        )}

        {/* Categorías de Academia */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          {academiaCategories.map(cat => {
            const IconComponent = cat.icon;
            const isActive = selectedCategory === cat.id;
            const count = cat.id === 'all' 
              ? contents.length 
              : contents.filter(c => c.academia_category === cat.id).length;
            
            return (
              <button
                key={cat.id}
                onClick={() => setSelectedCategory(cat.id)}
                className={`p-6 rounded-2xl transition-all flex flex-col items-center gap-3 border ${
                  isActive 
                    ? `${cat.color} text-white border-transparent shadow-lg scale-105` 
                    : 'bg-zinc-900/50 text-zinc-400 border-zinc-800 hover:border-pink-500/30 hover:text-white'
                }`}
              >
                <IconComponent className="w-8 h-8" />
                <span className="font-bold">{cat.label}</span>
                <span className={`text-sm px-3 py-1 rounded-full ${
                  isActive ? 'bg-white/20' : 'bg-zinc-800'
                }`}>
                  {count} {count === 1 ? 'video' : 'videos'}
                </span>
              </button>
            );
          })}
        </div>

        {/* Mensaje para usuarios no logueados */}
        {!user && (
          <div className="bg-zinc-900/50 border border-zinc-800 rounded-3xl p-8 mb-12 text-center">
            <p className="text-lg text-zinc-300 mb-4">Algunos videos están disponibles solo para miembros</p>
            <a href="/programs">
              <Button className="bg-pink-600 hover:bg-pink-500 text-white rounded-full px-8 py-3 font-bold">
                Ver Programas
              </Button>
            </a>
          </div>
        )}

        {/* Filtros adicionales */}
        <div className="flex flex-col md:flex-row gap-4 mb-12">
          <div className="flex-1 relative">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-zinc-500" />
            <Input
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Buscar contenido..."
              className="pl-12 rounded-full py-6 bg-zinc-900 border-zinc-800 text-white placeholder:text-zinc-500 focus:border-pink-500"
            />
          </div>
          <div className="flex gap-2 flex-wrap">
            {levels.map(level => (
              <button
                key={level}
                onClick={() => setSelectedLevel(level)}
                className={`px-4 py-2 rounded-full font-medium text-sm transition-all ${
                  selectedLevel === level
                    ? 'bg-pink-600 text-white'
                    : 'bg-zinc-800 text-zinc-400 hover:bg-zinc-700 hover:text-white border border-zinc-700'
                }`}
              >
                {level === 'all' ? 'Todos' : levelNames[level]?.split(' - ')[0] || level}
              </button>
            ))}
          </div>
        </div>

        {/* Sin contenido */}
        {sortedContents.length === 0 ? (
          <div className="text-center py-16 border border-dashed border-zinc-800 rounded-3xl">
            <Video className="w-16 h-16 text-zinc-700 mx-auto mb-4" />
            <h3 className="text-xl font-bold text-white mb-2">No hay contenido disponible</h3>
            <p className="text-zinc-500">
              {searchTerm ? 'No se encontraron resultados para tu búsqueda' : 'Pronto agregaremos nuevo contenido educativo'}
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {sortedContents.map((content) => {
              const hasAccess = canAccessContent(content);
              const canBuyIndividually = content.price && content.price > 0;
              const contentLevels = content.levels || [content.level];
              
              const getAccessStatus = () => {
                if (contentLevels.includes('free')) return { text: 'Gratis', color: 'bg-green-500/20 text-green-400 border-green-500/30' };
                if (hasAccess) return { text: 'Tu nivel', color: 'bg-blue-500/20 text-blue-400 border-blue-500/30' };
                return { text: 'Upgrade necesario', color: 'bg-amber-500/20 text-amber-400 border-amber-500/30' };
              };
              const accessStatus = getAccessStatus();
              
              return (
                <div
                  key={content.content_id}
                  className="group bg-zinc-900/50 border border-zinc-800 rounded-3xl overflow-hidden hover:border-pink-500/50 transition-all"
                >
                  {/* Preview */}
                  <div className="aspect-video bg-zinc-800 relative flex items-center justify-center overflow-hidden">
                    {content.cover_image || content.thumbnail_url ? (
                      <img 
                        src={(content.cover_image || content.thumbnail_url).startsWith('/api') ? `${BACKEND_URL}${content.cover_image || content.thumbnail_url}` : (content.cover_image || content.thumbnail_url)}
                        alt={content.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                    ) : content.content_type === 'image' ? (
                      <img 
                        src={content.file_path.startsWith('/api') ? `${BACKEND_URL}${content.file_path}` : content.file_path}
                        alt={content.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                    ) : content.content_type === 'video' ? (
                      <Video className="w-16 h-16 text-zinc-600" />
                    ) : (
                      <FileText className="w-16 h-16 text-zinc-600" />
                    )}

                    {!hasAccess && (
                      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm flex flex-col items-center justify-center">
                        <Lock className="w-10 h-10 text-white mb-2" />
                        <span className="text-sm font-medium text-white">Contenido Bloqueado</span>
                      </div>
                    )}

                    {/* Badge de tipo */}
                    <span className={`absolute top-3 right-3 text-xs font-bold px-3 py-1 rounded-full ${
                      content.content_type === 'video' ? 'bg-pink-500 text-white' : 
                      content.content_type === 'image' ? 'bg-purple-500 text-white' : 'bg-blue-500 text-white'
                    }`}>
                      {content.content_type === 'video' ? 'Video' : 
                       content.content_type === 'image' ? 'Imagen' : 'PDF'}
                    </span>

                    {/* Badge de precio si tiene */}
                    {!hasAccess && canBuyIndividually && (
                      <span className="absolute top-3 left-3 text-xs font-bold px-3 py-1 rounded-full bg-green-500 text-white">
                        ${content.price}
                      </span>
                    )}
                  </div>

                  {/* Info */}
                  <div className="p-6">
                    <h3 className="font-barlow font-bold text-white text-lg mb-2 line-clamp-2">
                      {content.title}
                    </h3>
                    <p className="text-sm text-zinc-500 mb-4 line-clamp-2">
                      {content.description || 'Contenido educativo de la academia'}
                    </p>

                    {/* Etiqueta de estado de acceso */}
                    <div className={`inline-block px-3 py-1 rounded-full text-xs font-bold mb-4 border ${accessStatus.color}`}>
                      {accessStatus.text}
                    </div>

                    {hasAccess ? (
                      <button 
                        onClick={() => navigate(`/academia/${content.content_id}`)}
                        className="flex items-center justify-center gap-2 w-full bg-pink-600 hover:bg-pink-500 text-white rounded-full py-3 font-bold transition-colors"
                        data-testid={`view-content-${content.content_id}`}
                      >
                        <Play className="w-4 h-4" />
                        {content.content_type === 'video' ? 'Ver Ahora' : content.content_type === 'image' ? 'Ver Imagen' : 'Ver Contenido'}
                      </button>
                    ) : (
                      <div className="space-y-2">
                        <button
                          onClick={() => navigate(`/academia/${content.content_id}`)}
                          className="flex items-center justify-center gap-2 w-full bg-zinc-800 hover:bg-zinc-700 text-white border border-zinc-700 rounded-full py-3 font-bold transition-colors"
                        >
                          <ArrowUp className="w-4 h-4" />
                          Ver Detalles
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* CTA Final */}
        <div className="mt-16 bg-gradient-to-r from-pink-600/20 to-purple-600/20 border border-pink-500/30 rounded-3xl p-12 text-center">
          <h2 className="font-barlow text-3xl md:text-4xl font-bold text-white uppercase mb-4">
            ¿Quieres acceso completo?
          </h2>
          <p className="text-lg text-zinc-400 mb-6">
            Obtén la membresía premium y desbloquea todo el contenido educativo
          </p>
          <a href="/programs">
            <Button className="bg-pink-600 hover:bg-pink-500 text-white rounded-full px-12 py-6 text-lg font-bold glow-pink">
              Ver Membresía Premium
              <ChevronRight className="w-5 h-5 ml-2" />
            </Button>
          </a>
        </div>
      </div>
    </div>
  );
};

export default Academia;
